import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { lineApi } from '../../api/client';
import { getToday } from '../../lib/dateUtils';
import { X, RefreshCw, Send, Trash2 } from 'lucide-react';

export default function LineSendPanel({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [date, setDate] = useState(getToday());
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const textareaRef = useRef(null);

  // Auto-load today's message when panel opens
  useEffect(() => {
    if (isOpen) {
      const today = getToday();
      setDate(today);
      setMessage('');
      setSendResult(null);
      fetchPreview(today);
    }
  }, [isOpen]);

  const fetchPreview = async (d) => {
    setIsLoading(true);
    setSendResult(null);
    try {
      const res = await lineApi.preview(d);
      setMessage(res.data.message || '');
    } catch (err) {
      console.error('Preview error:', err);
      setMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDate(newDate);
    fetchPreview(newDate);
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    setIsSending(true);
    setSendResult(null);
    try {
      await lineApi.sendDaily(date, message);
      setSendResult({ success: true });
    } catch (err) {
      setSendResult({ success: false, error: err.response?.data?.error || err.message });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {/* Backdrop on mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={[
          'fixed top-16 bottom-0 w-80 bg-white shadow-2xl z-30 flex flex-col',
          'transition-transform duration-300 ease-in-out',
          // Desktop: slide from left-64; Mobile: slide from left-0
          'left-0 lg:left-64',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-tpc-blue text-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            <span className="font-semibold text-sm">{t('line.title', 'LINE 通報')}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Date picker */}
        <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            {t('reports.reportDate', '報表日期')}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={date}
              onChange={handleDateChange}
              className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-tpc-blue"
            />
            <button
              onClick={() => fetchPreview(date)}
              disabled={isLoading}
              title={t('common.refresh', '重新載入')}
              className="p-1.5 rounded border border-gray-300 hover:bg-gray-100 text-gray-600 disabled:opacity-40 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Message textarea */}
        <div className="flex-1 flex flex-col min-h-0 px-4 py-3">
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-gray-500">
              {t('line.preview', '訊息內容')}
            </label>
            <button
              onClick={() => { setMessage(''); setSendResult(null); textareaRef.current?.focus(); }}
              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 transition-colors"
              title={t('common.clear', '清除')}
            >
              <Trash2 className="w-3 h-3" />
              {t('common.clear', '清除')}
            </button>
          </div>

          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-tpc-blue" />
            </div>
          ) : (
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => { setMessage(e.target.value); setSendResult(null); }}
              className="flex-1 w-full border border-gray-300 rounded p-2 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-tpc-blue"
              placeholder={t('line.noData', '暫無資料')}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 flex-shrink-0 space-y-2">
          {sendResult && (
            <div className={`text-xs px-3 py-2 rounded ${sendResult.success ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {sendResult.success
                ? t('line.sendSuccess', '已成功發送至 LINE 群組')
                : (sendResult.error || t('line.sendFailed', '發送失敗'))}
            </div>
          )}
          <button
            onClick={handleSend}
            disabled={isSending || isLoading || !message.trim()}
            className="w-full flex items-center justify-center gap-2 bg-tpc-blue hover:bg-tpc-blue-dark disabled:opacity-40 text-white text-sm font-medium py-2 rounded transition-colors"
          >
            {isSending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {t('line.sendDaily', '發送至 LINE')}
          </button>
        </div>
      </div>
    </>
  );
}
